<script lang="ts" setup generic="TObj extends BaseObj, TDetailObj extends BaseObj = any">
import type { BaseObj } from '@/objects/BaseObj';
import { computed, onActivated, ref, watch, type Component, onDeactivated, provide, toRaw, nextTick } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import type ListCompExpose from '@/objects/ListCompExpose';
import {
    GridComp,
    ValidatedText,
    ClickIconRenderer,
    ListDetailModal,
    ListCompToolbar,
    InvalidValuesMessage,
    DidYouKnowMessage,
    GridActionHeader,
} from '@/components';
import { useDebouncer } from '@/utilities/Debouncer';
import type { GuiDef } from '@/objects/gui/GuiDef';
import type { FieldDef } from '@/objects/gui/FieldDef';
import {
    GridSetup,
    type IGridCompExposed,
    GridColumnOptions,
    GridColumnDefinition,
    type IGridColumnSort,
    GridDataSource,
} from '../Grid/Classes';
import { TaskLoaderState, useDialog, useKeyboardListener, Validator } from '@/composeables';
import { useAccessStore, useUtilStore } from '@/stores';
import type { IDetailModalExposed } from '../Detail/IDetailModalExposed';
import AlertMessage from '../Alert/AlertMessage.vue';
import { $tm } from '@/locize';
import { ValidatorDef } from '@/objects/gui';
import type { IGridRowGetterParams } from '../Grid/Classes/GridDataSource';
import type { ModalSize } from '../Modal/ModalProps';
import { arrayUnique } from '@/utilities';
import type { IGridRowCssRule } from '../Grid/Classes/IGridRowCssRule';
import type { IGridGroupCols } from '../Grid/Classes/IGridGroupCols';
import { DropdownItemButton, DropdownItemCustom, DropdownItemMenu, DropdownItemSeparator, type DropdownItemTypes } from '@/objects';
import type { IGridDetailExposed } from '../Grid/Classes/IGridDetailExposed';
import { ListCompState } from './ListCompState';
import { useElementVisibility } from '@vueuse/core';
import { Router } from '@/objects/Router';
import { type EntityState } from '@/objects/generated';
import { getRouterStoreRef } from '@/stores/routerStore';
import type { CustomGridState } from '../Grid/Classes/CustomGridState';

const accessStore = useAccessStore();

// The grid component
const gridComp = ref<IGridCompExposed<TObj> | null>(null);

const container = ref<HTMLElement | null>(null);

// We need to know if the list is visible or not
const isVisible = useElementVisibility(container);

// descriptions
const props = withDefaults(
    defineProps<{
        /**
         * The GUI definition for the list
         */
        gui: GuiDef<TObj>;

        /**
         * The model value / List of rows for the list
         */
        modelValue: Array<TObj>;

        /**
         * A list of items in the list that are selected
         */
        selected?: Array<TObj>;

        /**
         * The detail view to show when a row is clicked
         * If this is null, the detail view will not be shown
         * @default null
         */
        detailView?: Component<{ id: number }> | null;

        /**
         * Additional props to pass to the detail view component
         */
        detailViewParams?: any;

        /**
         * Overridden size of modal for detail
         */
        detailSize?: ModalSize;

        /**
         * The quick buttons to show in the toolbar
         * @default []
         */
        quickButtons?: Array<DropdownItemButton | DropdownItemSeparator | DropdownItemMenu | DropdownItemCustom<any, any>>;

        /**
         * The menu actions to show in the toolbar
         * @default []
         */
        menuActions?: Array<DropdownItemButton | DropdownItemSeparator | DropdownItemMenu | DropdownItemCustom<any, any>>;

        /**
         * The filter function to use for the list
         * @default []
         */
        filter?: Array<(obj: TObj, searchstringlowercase: string) => boolean>;

        /**
         * The placeholder for the filter
         * should be a translation key
         * @default ''
         */
        filterPlaceholder?: string;

        /**
         * If the list should be sorted either by a function or by default
         * @default true
         */
        sort?: boolean | ((a: TObj, b: TObj) => number) | any; // TODO: We probably want default on this and this col here, false will mean no sorting

        /**
         * If the default buttons should be hidden. This is the save, refresh, add and delete buttons
         * @default false
         */
        hideDefaultButtons?: boolean;

        /**
         * Custom columns to add to the grid
         * These are columns that are not defined in the Gui definition
         * @default []
         */
        customColumns?: Array<GridColumnOptions<TObj>> | ((columns: Array<GridColumnOptions<TObj>>) => Array<GridColumnOptions<TObj>>);

        /**
         * Autoresize columns after data is loaded
         * @default false
         */
        autoresizeColumns?: boolean;

        /**
         * Shows the save state on the save button
         */
        savestate?: TaskLoaderState;

        /**
         * If true, the grid will mark rows as dirty when they are added and recomend you save them
         * @default false
         */
        markDirtyRows?: boolean;

        /**
         * If we want checkboxes in the grid with a select all checkbox in the header
         * @default false
         */
        selectAll?: boolean;

        actionMenuText?: string;

        /**
         * Hides the searchfield from the tollbar
         * @default false
         */
        hideSearchField?: boolean;

        /**
         * If we want to show the toolbar or not
         * @default false
         */
        hideToolbar?: boolean;

        /**
         * The row selection mode for the grid
         * @default 'multiple'
         */
        rowselection?: 'multiple' | 'single' | undefined;

        /**
         * If the list is readonly
         */
        readonly?: boolean;

        /**
         * A unique identifier for the grid. This will enable the grid to remember the column sizes and order and state
         */
        gridkey?: string;

        /**
         * The number of rows to be rendered in each batch
         * @default 100
         */
        numberOfRenderedRows?: number;

        /**
         * The row css rules to apply to the grid
         */
        rowClassRules?: IGridRowCssRule<TObj>[];

        /**
         * We can send a string that we want to filter on
         */
        searchText?: string;

        /**
         * Render the list mode of the list
         * This will make it more like a list than a grid
         * @remarks Works only with readonly
         * @default false
         *
         */
        listMode?: boolean;

        /**
         * Hide the statusbar of the grid
         * @default false
         */
        hideStatusbar?: boolean;

        /**
         * The resize strategy for the grid
         * @default 'fitToContent' The grid will try and resize to fit the content
         */
        resizeStrategy?: 'fitToContent' | 'sizeColumnsToFit';

        saveStateLoader?: (loader: () => Promise<void>, key: string, icon: string | null, singleloader?: boolean) => Promise<void>;

        /**
         * The grid setup for the detail grid
         * This will create a grid within the detail view
         */
        gridDetailSetup?: GridSetup<TDetailObj>;

        /**
         * The detail view to show when a row is clicked
         * If this is null, the detail view will not be shown
         * @default null
         */
        gridDetailView?: Component<IGridDetailExposed<TDetailObj>>; // This need to implement the IGridDetailExposed interface

        /**
         * Never show the messages. This will hide the Changes, Field error and Tip messages.
         * This will not affect the cell colors and messages for validations within the grid, only the messageboxes that are displayed on top of the grid
         * @default false
         */
        hideMessages?: boolean;

        /**
         * he title for the detail view in the grid
         * @default undefined
         */
        gridDetailTitle?: string;

        /**
         * We show an action dropdown instead of the select all checkbox
         * @remarks If no actions are set, we use the default actions. Add, Delete, Select All, Refresh
         * @default false
         */
        useActionHeader?: boolean;

        /**
         * Custom actions for the grid action header
         * @remarks If this one is set the gridActionHeader will be set to true. This is to avoid having to set both.
         * @default undefined
         */
        gridActionHeaderActions?: DropdownItemTypes[];

        /**
         * If the modal should stay open after saving
         * @default false
         */
        keepOpenOnSave?: boolean;

        /**
         * Custom GUI fields for the object.
         * @param fields The fields to customize.
         */
        customGuiFields?: (fields: FieldDef<TObj>[]) => FieldDef<TObj>[];

        /**
         * Hide deleted items from the list
         * @default false
         */
        hideDeleted?: boolean;

        /**
         * Will not add the default menu actions to the toolbar
         */
        hideDefaultMenuActions?: boolean;

        /**
         * User supplied rowIdGetter for the ListComp, if we are using an object that does not have an ID field
         */
        rowIdGetter?: (obj: TObj) => string;

        /**
         * Actions to show in the footer before the close and save buttons
         */
        detailFooterActions?: Array<DropdownItemButton<(id: number) => any | Promise<any>>>;

        /** Text to show in addition when unsaved warning is shown */
        additionalWarningOnChange?: string;
    }>(),
    {
        detailView: null,
        detailId: null,
        quickButtons: () => [],
        menuActions: () => [],
        filter: () => [],
        selected: () => [],
        sort: true,
        hideDefaultButtons: false,
        customColumns: () => [],
        autoresizeColumns: false,
        markDirtyRows: false,
        selectAll: false,
        hideSearchField: false,
        rowselection: 'multiple',
        hideToolbar: false,
        numberOfRenderedRows: 100,
        listMode: false,
        resizeStrategy: 'fitToContent',
        hideStatusbar: false,
        hideMessages: false,
        gridActionHeader: false,
        keepOpenOnSave: false,
        hideDeleted: false,
        hideDefaultMenuActions: false,
    },
);

const computedModelValue = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

/**
 * The ID of the object to load in the detail view
 * @remarks The value has to be either negative ( i.e a new object ) or positive ( i.e an existing object ) null means no modal
 */
const currentDetailId = ref<number | null>(null);

const alertmessageElement = ref<typeof AlertMessage | null>(null);
const invalidValuesElement = ref<typeof InvalidValuesMessage | null>(null);
const toolbarElement = ref<typeof ListCompToolbar | null>(null);

const invalidRowsAndFields = ref<Map<string, string[]> | null>(null);

const numberOfRowsWithNotifications = ref<number>(0);

// get groups from gui and add custom groups
const guiFields = computed(() => (props.customGuiFields ? props.customGuiFields(props.gui.Fields) : props.gui.Fields));

/**
 * Validate the column values in the grid with the validators in the guidef
 * @param colums
 */
const validateColumnValues = (colums?: string[]) => {
    let hasInvalidFields = false;

    const fields = guiFields.value.filter((f) => !colums || colums.includes(f.FieldName));

    const _state = toRaw(listCompState.value);

    const rowWithNotification = new Set<string>();

    // First check if there is any validator on the guidef
    for (const field of fields) {
        // We reset the field state
        _state.InvalidFields.set(field.FieldName, []);
        _state.WarningFields.set(field.FieldName, []);

        if (field.Validator && field.Validator instanceof ValidatorDef) {
            // Loop the data and validate the field
            for (const row of computedModelValue.value) {
                const validator = new Validator(field.Validator);
                const validatedres = validator.Validate(field.value(row), row);
                if (validatedres === 'invalid') {
                    hasInvalidFields = true;
                    _state.InvalidFields.get(field.FieldName)?.push(gridSetup.rowIdGetter!(row));
                    rowWithNotification.add(gridSetup.rowIdGetter!(row));

                    console.trace(`Invalid value in field ${field.FieldName} with value ${field.value(row)}`, row);
                } else if (validatedres === 'warning') {
                    // We could handle warnings here if needed
                    _state.WarningFields.get(field.FieldName)?.push(gridSetup.rowIdGetter!(row));
                    rowWithNotification.add(gridSetup.rowIdGetter!(row));
                }
            }
            if (hasInvalidFields) {
                break;
            }
        }
    }

    listCompState.value.HasInvalidRows = hasInvalidFields;
    listCompState.value.InvalidFields = _state.InvalidFields;
    listCompState.value.WarningFields = _state.WarningFields;
    numberOfRowsWithNotifications.value = rowWithNotification.size;

    // We flatt the state.invalidfields map to a list of strings from the valus
    let numberOfInvalidFields =
        (Array.from(_state.InvalidFields.values()).flat()?.length ?? 0) + (Array.from(_state.WarningFields.values()).flat()?.length ?? 0);

    if (numberOfInvalidFields > 0) {
        const mergedMap = new Map<string, string[]>();
        for (const [key, value] of _state.InvalidFields.entries()) {
            mergedMap.set(key, value);
        }
        for (const [key, value] of _state.WarningFields.entries()) {
            if (mergedMap.has(key)) {
                mergedMap.set(key, mergedMap.get(key)!.concat(value));
            } else {
                mergedMap.set(key, value);
            }
        }
        invalidRowsAndFields.value = mergedMap;
    } else {
        invalidRowsAndFields.value = null;
    }

    emit('datastate', !hasInvalidFields);
};

/**
 * Check if the dataset has been changed
 */
const listCompState = ref<ListCompState>(new ListCompState());

/**
 * Callback whenever a cell value is changed
 * @param row
 * @param columnId
 * @param newVal
 */
const onCellValueChanged = (row: TObj, columnId: string, newVal: any, oldVal: any) => {
    validateColumnValues();

    setChangedState(true);

    // TODO: This should be just one row
    gridComp.value?.refreshCachedData();

    emit('cellValueChanged', row, columnId, newVal, oldVal);
};

/**
 * Define the emits for the component
 */
const emit = defineEmits<{
    (e: 'update:modelValue', obj: Array<TObj>): void;

    /**
     * Save the detail
     */
    (e: 'saveDetail', obj: TObj, refreshOnlyIds?: number[]): void;

    /**
     * Save the list
     */
    (e: 'saveList', valid: boolean): void;

    /**
     * If the data is valid or not
     */
    (e: 'datastate', valid: boolean): void;

    /**
     * Refresh the list
     */
    (e: 'refreshList'): void;

    /**
     * Delete the selected rows
     */
    (e: 'deleteselected'): void;

    /**
     * Whenever the selection changes
     */
    (e: 'onSelectionChanged', selectedRows: TObj[]): void;

    /**
     * Add a new row in the grid
     */
    (e: 'addInGrid'): void;
    (e: 'objHovered', obj: TObj | null): void;
    (e: 'rowschanged'): void;
    (e: 'cellKeyDown', event: KeyboardEvent): void;

    (e: 'datachanged', value: boolean): void;

    /**
     * If the detail view is
     */
    (e: 'detailchanged', value: boolean): void;

    /**
     * Emits whenever a cell is changed
     */
    (e: 'cellValueChanged', row: TObj, columnId: string, newVal: any, oldVal: any): void;

    // We emit this when the detailModal has loaded a new item
    (e: 'detailLoaded', value: TObj | TDetailObj): void;

    (e: 'onCellEditingStopped', row: TObj, columnId: string, rowId?: string): void;
    (e: 'onCellEditingStarted', row: TObj, columnId: string, rowId?: string): void;
}>();

// Add a keyboard shortcut to open the detail view from the list
useKeyboardListener().addKeyboardShortcut({
    on: 'CTRL+ENTER',
    action: () => (lastFocusedRowId.value != null ? openDetail(lastFocusedRowId.value) : null),
});

const detailModal = ref<IDetailModalExposed>();

// Keep a list of the selected rows
const onSelectionChanged = (selectedRows: TObj[]) => {
    emit('onSelectionChanged', selectedRows);
};

// Emit the object that is hovered
const onCellMouseOver = (row: TObj) => emit('objHovered', row);

// Emit null when the mouse is out.
const onCellMouseOut = (row: TObj) => emit('objHovered', null);

// saving of detail requested
const saveDetail = (value: TObj, refreshOnlyIds?: number[]) => emit('saveDetail', value, refreshOnlyIds);

const onCellEditingStarted = (row: TObj, columnId: string, rowId?: string) => {
    emit('onCellEditingStarted', row, columnId, rowId);
};

const onCellEditingStopped = (row: TObj, columnId: string, rowId?: string) => {
    emit('onCellEditingStopped', row, columnId, rowId);
};

/**
 * Open the detail view
 * @param id
 */
const openDetail = (id: number) => {
    Router.NavigateTo({ id: id });
};

/**
 * This is called when the detailCompHas loaded a new item
 * @param detailCompItem
 */
const onDetailLoaded = (detailCompItem: TObj | TDetailObj) => emit('detailLoaded', detailCompItem);

/**
 * Open an empty modal with a new object
 */
const showDetail = () => {
    nextTick(() => detailModal.value?.show());
};

// Closing the detail view
const closeDetail = () => {
    detailModal.value?.hide();
};

/**
 * The default layout for the toolbar
 */
const toolbar = {
    /**
     * The refresh list callback for the refresh button
     */
    refreshList: () => {
        validateColumnValues();
        emit('refreshList');
    },

    resizeColumns: () => gridComp.value?.resizeColumns(true),

    /**
     * The custom menu actions in the toolbar dropdown
     */
    menuActions: props.menuActions,

    /**
     * The list of quick buttons to show in the toolbar
     */
    quickButtons: props.quickButtons,

    /**
     * The value of the search text
     */
    searchText: ref(''),

    /**
     * The placeholder for the search box
     */
    searchBoxPlaceholder: props.filterPlaceholder,

    /**
     * If we should hide the default buttons in the toolbar i.e save, refresh, add and delete
     */
    hideDefaultButtons: props.hideDefaultButtons,

    /**
     * If we should hide the search field
     * @remarks This will hide the search field and the search button
     * @default false
     */
    hideSearchField: props.hideSearchField,

    /**
     * Callback for when the filter errors button is clicked
     */
    filterErrors: (state: boolean) => showRowsWithError(state),
};

/**
 * Helper function to set the state of the list
 * @param value
 */
const setChangedState = (value: boolean) => {
    listCompState.value.IsChanged = value;
    emit('datachanged', value);
};

/**
 * We show only the rows that has not passed validation
 */
const showRowsWithError = (state: boolean) => {
    listCompState.value.IsFilteredInvalidRows = state;

    if (!state) {
        gridComp.value?.purgeCachedData();
    } else {
        gridComp.value?.refreshCachedData();
    }
};

let currentRowgetterParams: IGridRowGetterParams<TObj> | null = null;

const filteredRows = (values: TObj[], extraFilter?: (r: TObj) => boolean) => {
    const searchValue = (props.searchText ?? toolbar.searchText.value).toLocaleLowerCase();

    const filtersToRun: Array<(r: TObj) => boolean> = [];

    // remove deleted items if hideDeleted is true
    if (props.hideDeleted) {
        filtersToRun.push((r) => r.EntityState !== 'Deleted');
    }

    if (listCompState.value.IsFilteredInvalidRows) {
        filtersToRun.push((r) => {
            let hasInvalidField = false;
            for (const [key, value] of invalidRowsAndFields.value?.entries() ?? []) {
                if (value.includes(gridSetup.rowIdGetter!(r))) {
                    hasInvalidField = true;
                    break;
                }
            }
            return hasInvalidField;
        });
    } else {
        filtersToRun.push((r) => {
            // We always show newly added rows.
            // This is neccessary to avoid we add a new row but it doesnt show because we have set a filter that removes the default value
            if (r.EntityState == 'Added') {
                return true;
            }

            if (extraFilter && !extraFilter(r)) {
                return false;
            }

            // TODO: Tror vi må definere opp filtrene våre selv. Og dermed få dette ut her
            // Disse filterne kan vi vel la GridComp ta seg av?
            // Ved behov kan vi legge ved en filtrerings funksjon i kolonne definisjonen

            let filters = props.filter ?? [];

            // Add the default filter
            if (filters.length == 0 && searchValue != '') {
                filters.push((r: TObj, searchValue: string) => {
                    for (const value of Object.values(r)) {
                        if (typeof value != 'string' && typeof value != 'number') {
                            continue;
                        }

                        if (value.toString().toLowerCase().includes(searchValue)) {
                            return true;
                        }
                    }
                    return false;
                });
            }

            for (const f of props.filter) {
                if (!f(r, searchValue)) {
                    return false;
                }
            }

            return true;
        });
    }

    // filter only once
    values = values.filter((r) => {
        for (const f of filtersToRun) {
            if (!f(r)) {
                return false;
            }
        }
        return true;
    });

    // We run the filters on the grid
    if (currentRowgetterParams !== null) {
        values = currentRowgetterParams.filter(values);
    }

    return values;
};

const rowCounterValue = ref(computedModelValue.value.length);

/**
 * Set up the datasource for the grid
 */
const dataSource = new GridDataSource<TObj>({
    totalNumberOfRows: () => computedModelValue.value.length, // Maybe this needs to change for a server side data source

    rowCountGetter: () => rowCounterValue.value,

    rowGetter: (params: IGridRowGetterParams<TObj>) => {
        currentRowgetterParams = params;

        // We check if the group is collapsed, if it is we dont show the rows
        // TODO: Here we should have a setting to be able to show all groups expanded
        let isCollpased = (params.groupKeys?.length ?? 0) == 0;

        // Grouping
        let currentGroupKeys: string[] = params.groupKeys ?? []; // Contains the groukeys we have pressed. This is used to filter the data
        let groupColumn: IGridGroupCols | null = (params.rowGroupCols?.length ?? 0) > 0 ? params.rowGroupCols![0]! : null; // Contains the group column we are grouping on

        let filtered = filteredRows(computedModelValue.value);

        // remove deleted items if hideDeleted is true
        if (props.hideDeleted) {
            filtered = filtered.filter((r) => r.EntityState !== 'Deleted');
        }

        // We filter the rows with errors
        if (listCompState.value.IsFilteredInvalidRows) {
            filtered = filtered.filter((r) => {
                let hasInvalidField = false;
                for (const [key, value] of invalidRowsAndFields.value?.entries() ?? []) {
                    if (value.includes(gridSetup.rowIdGetter!(r))) {
                        hasInvalidField = true;
                        break;
                    }
                }
                return hasInvalidField;
            });
        }
        // If there is any columns with groupings we need to handle this
        else if ((params.rowGroupCols?.length ?? 0) > 0 && isCollpased) {
            // If no groups are selected, that means we are only shoing the group header
            if (isCollpased) {
                // We need to find one unique value in the group to be able to show it
                let isGroupKeyAdded = new Set<string>();

                // We need to get the unique value of the group. For now we only support one level of grouping. That means that there should only be one rowGroupCols

                // We loop through all the rows and find the unique values for the groupColumn. ( this should be case insensitive)
                filtered = filtered.filter((r) => {
                    // What is the best way of getting the value of the groupColumn? Should we return the valuegetter from the grid?

                    let value = (r as unknown as any)[groupColumn!.dataField]?.toString()?.toLowerCase();
                    if (isGroupKeyAdded.has(value)) {
                        return false;
                    }
                    isGroupKeyAdded.add(value);
                    return true;
                });
            }
        } else {
            filtered = filteredRows(computedModelValue.value, (r) => {
                // We need to check if we have group columns, if we have we need to filter on the group columns
                // TODO: Since we only support one level of grouping for now we know that the key is always on the first level
                // We should probably make this more dynamic
                if (currentGroupKeys.length > 0) {
                    // Get the value of the group column
                    let groupColValue = (r as unknown as any)[groupColumn!.dataField]?.toString()?.toLowerCase();
                    if (!currentGroupKeys.includes(groupColValue)) {
                        return false;
                    }
                }
                return true;
            });

            // We run the filters on the grid
            filtered = params.filter(filtered);

            // if we override sorting we run that instead og the default sorting, we probably could set this as the sort function in the grid setup but i cant see how it is done
            if (props.sort instanceof Function) {
                filtered.sort(props.sort);
            } else {
                params.sort(filtered);
            }
        }
        const slice = filtered.slice(params.startRow, params.endRow);

        rowCounterValue.value = filtered.length;

        params.successCallback(slice, filtered, filtered.length);
    },
});

/**
 * This is the last row that has had focus
 */
const lastFocusedRowId = ref<number | null>(null);

/**
 * Raised every time a cell changes focus
 * @param row
 * @param columnId
 */
const onCellFocused = (rowId: string) => {
    lastFocusedRowId.value = parseInt(rowId);
    return null;
};

/**
 * Callback for the grid when the data is loaded
 */
const onFirstDataRendered = () => {
    if (props.autoresizeColumns) {
        gridComp.value?.resizeColumns();
    }
};

/**
 * When the detail modal is closed
 */
const detailClosed = () => {
    nextTick(() => {
        Router.NavigateTo({ tab: Router.RouteParams?.tab, id: undefined, subtab: undefined });
    });
};

/**
 * Whenever the enter or tab is pressed in the search box we set the focus to the first cell in the grid
 */
const onSearching = () => {
    gridComp.value?.focus('first');
};

/**
 * Get the column definition for a field
 * @param fieldDef
 */
const getColumn = (fieldDef: FieldDef<TObj>): GridColumnOptions<TObj> => {
    return new GridColumnOptions<TObj>({
        dataField: fieldDef.FieldName,
        colId: fieldDef.Name,
        editable: props.listMode ? false : fieldDef.Editable === false ? false : props.readonly ? false : fieldDef.Editable, // If fieldDef is explicitly set to false, we should not be able to edit it no matter what, or else we can set it to false if the list is readonly
        minWidth: fieldDef.MinWidth,
        maxWidth: fieldDef.MaxWidth,
        resizable: !props.listMode,
        filterable: props.listMode ? false : fieldDef.Filterable,
        sortable: props.listMode ? false : fieldDef.Sortable,
        suppressMovable: props.listMode ? true : false,
        cellClassRules: fieldDef.cellClassRules,
        sortdirection: fieldDef.SortDirection,
        columnHeaderText: fieldDef.TranslationKey ?? fieldDef.FieldName,
        headerComponentParams: { listmode: props.listMode, tooltiptext: fieldDef.Tooltip, filterable: fieldDef.Filterable },
        cellRenderer: fieldDef.CellRenderer ?? ValidatedText,
        cellRendererParams: fieldDef.CellRendererPropsGetter ?? { ...fieldDef.CellRendererProps, validator: fieldDef.Validator },
        cellEditor: fieldDef.CellEditor,
        cellEditorParams: fieldDef.CellEditorPropsGetter ?? { ...fieldDef.CellEditorProps, validator: fieldDef.Validator },
        valueSetter: (obj, val) => {
            fieldDef.setValue(obj, val);
            return val;
        },
        valueGetter: fieldDef.value,

        // This should contain a list of all the unique values in the column
        filterValues:
            fieldDef?.HeaderFilterValueGetter !== undefined
                ? () => getTotalFilterdMap(fieldDef)
                : () => {
                      // This is the default filter values getter. It only gets the unique values in the column
                      let values = arrayUnique(computedModelValue.value.map((v) => fieldDef.value(v)));

                      return new Map<string, any>(values.map((v) => [v, v]));
                  },

        aggregate: fieldDef.Aggregate,
        valueFormatterForExport: fieldDef.ExportValueFormatter,
    });
};

/**
 * Gets the unique values for a column in the grid with a custom value getter
 * @param fieldDef
 */
const getTotalFilterdMap = (fieldDef: FieldDef<TObj>) => {
    if (!fieldDef.HeaderFilterValueGetter) {
        return new Map<string, any>();
    }
    let map = new Map<string, any>();
    computedModelValue.value.forEach((v) => {
        let val = fieldDef.value(v);
        if (val) {
            map.set(fieldDef.HeaderFilterValueGetter!(v), val);
        }
    });

    return map;
};

/**
 * Get the widht of the ID click renderer
 */
const getClickRendererWidth = () => {
    let width = 10;

    if (props.detailView) {
        width += 25;
    }

    if (props.gridDetailView) {
        width += 18;
    }

    return width;
};

/**
 * Define the cell renderer params for the row detail cell ( Checkboxes and icons )
 */
const rowDetailCellRendererParams = () => {
    return props.detailView
        ? {
              action: openDetail,
              icon: 'fa-file-pen',
              hasDetailGrid: props.gridDetailView !== undefined,
          }
        : { action: undefined, icon: '', hasDetailGrid: false };
};

const defaultMenuActions: (DropdownItemButton | DropdownItemSeparator)[] = [];
if (!props.hideDefaultMenuActions) {
    defaultMenuActions.push(
        DropdownItemButton.create({
            icon: 'fa-plus',
            action: () => emit('addInGrid'),
            translationkey: 'button.add',
        }),
    );
}

/**
 * Get the list of all selected rows in the grid
 */
const getSelectedRows = (): TObj[] => gridComp.value?.getSelectedRows() ?? [];

/**
 * The column for the row detail
 */
const rowDetailColumn = new GridColumnOptions<TObj>({
    sortable: false,
    filterable: false,
    resizable: false,
    editable: false,
    dataField: 'ID',
    hidden: !props.detailView,
    cellRenderer: ClickIconRenderer,
    cellRendererParams: rowDetailCellRendererParams(),
    maxWidth: getClickRendererWidth(),
    minWidth: getClickRendererWidth(),
    headerComponent: props.useActionHeader ? GridActionHeader : undefined,
    headerComponentParams: { actionItems: defaultMenuActions },
});

/**
 * Helper function to get the column definitions for the grid
 */
const gridColumnDefinitions = () => {
    const columns = [
        ...(props.detailView || props.selectAll ? [rowDetailColumn] : []),
        ...guiFields.value
            .filter(
                (field: FieldDef<TObj>) => field.IsDefinedFor('List') && !accessStore.IsHidden(props.gui.Name, field.FieldName), // TODO: this is now dependent on Event and its functionality so we should have it reactive
            )
            .map((field: FieldDef<TObj>) => {
                return getColumn(field);
            }),
    ];

    if (props.customColumns instanceof Function) {
        return props.customColumns(columns);
    }

    return [...columns, ...props.customColumns];
};

/**
 * We set up the default custom CSS rules for the grid
 * This also handles ettings the changed state of the list if we have changed rows
 */
const rowClassRules: IGridRowCssRule<TObj>[] = [
    {
        className: 'ag-row-dirty',
        func: (data) => {
            return props.markDirtyRows ? data.EntityState != 'Unchanged' && data.EntityState != 'Deleted' : false;
        },
    },
    {
        className: 'ag-row-deleted',
        func: (data) => {
            return data.EntityState == 'Deleted';
        },
    },
];
if (props.rowClassRules) {
    rowClassRules.push(...props.rowClassRules);
}

/**
 * Set up the grid
 */
const gridSetup = new GridSetup<TObj, TDetailObj>({
    gridkey: props.gridkey,
    rowIdGetter: (row: TObj) => {
        if (props.rowIdGetter) {
            return props.rowIdGetter(row);
        }

        let id;
        if ('ID' in row) {
            id = row.ID;
        } else if ('Id' in row) {
            id = row.Id;
        }

        if (typeof id === 'number') {
            return id.toString();
        } else if (typeof id === 'string') {
            return id;
        } else if (id === null) {
            // If the ID is null, then the row is corrupted somehow.
            // This actually happens if you open the contestantDialog, you get a row where all the fields are NULL.
            // This looks to be a Timepenalty, but its missing some of the fields. Like its an anonymous object, that has ID, Reason and Km all null
            // It also has a Penalty object with an aggregate. So it looks like it has something to do with the aggregate function in ContestantTimePenalty, but I havent figured it out
            // This really shouldnt be here, but ....
            return '';
        }
        throw new Error('No rowIdGetter defined for the grid and the object does not have an ID/Id field');
    },
    datasource: dataSource,
    rowSelection: props.rowselection,
    blockCacheSize: props.numberOfRenderedRows,
    hideStatusbar: props.hideStatusbar,
    hasCheckboxSelectionColumn: props.selectAll ? true : false,
    GridColumnDefinition: new GridColumnDefinition<TObj>({
        CommonColumnOptions: {
            sortable: props.sort != false && !(props.sort instanceof Function), // if this is a function we handle sort outside, we might split this in 2 variables so we can do both
            filterable: true,
            editable: true,
        },
        ColumnOptions: gridColumnDefinitions(),
    }),
    rowClassRules: rowClassRules,

    rowRefreshTrigger: (row?: TObj): EntityState => {
        if (!row) {
            return 'Unchanged';
        }
        return row.EntityState; // We refresh the row if the entity state changes. This is to update the dirty icon
    },

    // This is the DetailGrid that we want to show.
    detailGridSetup: props.gridDetailSetup,
});

/**
 * This handles the search debounce
 * We need to debounce this so we dont search on every keypress
 * In server side rendering we need to purge the grid cache when the new search ields a larger result set than the previous one
 * @param nv
 * @param ov
 */
const searchDebouncer = useDebouncer();
const searchDebounceHandler = (nv: any, ov: any) => {
    searchDebouncer(() => {
        if (ov.length > nv.length) {
            gridComp.value?.purgeCachedData();
        } else {
            gridComp.value?.refreshCachedData();
        }
    }, 50);
};

const filteredColumns = ref<string[]>([]);

/**
 * We listen to changes on the column filters and updates the gridstate accordingly
 */
watch(filteredColumns, (nv, ov) => {
    if (ov == null || ov.length == 0) return;
    // Hide th diff columns from nv to ov

    // find all columns in ov that are not in nv = new hidden
    const hiddenColumns = ov.filter((o) => !nv.includes(o));

    // find all columns in nv that are not in ov = new showm
    const shownColumn = nv.filter((n) => !ov.includes(n));

    if (hiddenColumns.length > 0) {
        gridComp.value?.hideColumns(hiddenColumns);
    }

    if (shownColumn.length > 0) {
        gridComp.value?.showColumns(shownColumn);
    }
});

/**
 * Keep the current active state of the component
 */
const viewActive = ref(true);
onActivated(() => (viewActive.value = true));
onDeactivated(() => (viewActive.value = false));

/**
 * We watch the search text from the toolbar
 */
watch(toolbar.searchText, (nv, ov) => searchDebounceHandler(nv, ov));

// we also watch the search text from the props
watch(
    () => props.searchText,
    (nv, ov) => searchDebounceHandler(nv, ov),
);

// This is used to set the selection from other components
// Specifically it is used in the StationRoles view to sync selection of the grid with the role boxes
watch(
    () => props.selected,
    () => gridComp.value?.selectRowsByRowId(props.selected.map(gridSetup.rowIdGetter!)),
);

// Watch for changes in the model value. This is only if we add or remove rows
watch(
    computedModelValue.value,
    () => {
        validateColumnValues();
        emit('rowschanged');
    },
    { immediate: true },
);

/**
 * Watch for changes in the detail view. We watch this to update the column definition for the row detail column.
 * Add the detail view icon if we have a detail view and change the width of the column
 */
watch(
    () => props.gridDetailView,
    (nv) => {
        let cellrenderParams = rowDetailCellRendererParams();
        cellrenderParams.hasDetailGrid = nv != undefined;

        rowDetailColumn.maxWidth = getClickRendererWidth();
        rowDetailColumn.minWidth = rowDetailColumn.maxWidth;
        rowDetailColumn.cellRendererParams = cellrenderParams;
    },
);

const { CurrentRoute } = getRouterStoreRef();

/**
 * We check if the route is changed and we have a current detail id
 */
watch(
    CurrentRoute,
    () => {
        if (!viewActive.value) {
            return;
        }
        if (CurrentRoute?.value?.params?.id != undefined) {
            // This mean we are opening a detail view
            if (CurrentRoute.value.params.id === 'new') {
                currentDetailId.value = useUtilStore().nextFakeId();
            } else {
                currentDetailId.value = CurrentRoute.value.params.id;
            }
        } else {
            // We dont have a detail id in the route
            currentDetailId.value = null;
        }
    },
    { immediate: true },
);

/**
 * Set up a cfmation dialog when the user tries to navigate away from the page and has unsaved changes
 */
onBeforeRouteLeave(async (to, from) => {
    if (listCompState.value.IsChanged) {
        const dialog = await useDialog().ConfirmClose();
        return await dialog.closePromise;
    }
});

/**
 * Whenever the state is updated. If the state is null it means we dont have a saved state and we use the default column order
 * @param state
 */
const onStateUpdated = (state: CustomGridState | null) => {
    if (state == null) {
        // We use the default column order
        const columnDefs = props.gridkey ? gridSetup.GridColumnDefinition.ColumnOptions : undefined;
        if (columnDefs) {
            filteredColumns.value = columnDefs.filter((col) => !col.hidden).map((col) => col.colId!);
        }
    } else {
        filteredColumns.value = state?.columnOrder?.filter((f) => !state?.hiddenColumns?.includes(f)) ?? [];
    }
};

/**
 * Expose the methods and properties of the list component
 */
defineExpose<ListCompExpose<TObj>>({
    dataUpdated: async (rowIds?: string[]) => {
        // TODO: Find a way to only refresh the rows that are updated
        await gridComp.value?.refreshCachedData();
    },
    getSelected: () => getSelectedRows(),
    deselectAll: () => gridComp.value?.deselectAllRows(),

    showDetail: showDetail,
    closeDetail: closeDetail,
    focusfirst: () => gridComp.value?.focus('first'),
    focuslastadded: () => gridComp.value?.focus('added'),
    resizeColumns: () => {
        gridComp.value?.resizeColumns();
    },
    stopEditing: () => {
        gridComp.value?.stopEditing();
    },

    startEditing: (columnId: string, rowId?: string) => {
        if (computedModelValue.value.length === 0) {
            // No rows to edit
            return;
        }
        // We get the rowid of the first row
        if (rowId === undefined) {
            rowId = gridSetup.rowIdGetter!(computedModelValue.value[0]!);
        }
        if (rowId === undefined) {
            return;
        }

        gridComp.value?.startEditing(rowId, columnId);
    },

    /**
     * Clear the grid has changes flag
     * NOTE! We need to expose this method to clear the changes in the grid since all the saving is done async on the outside with an emit event
     * @returns
     */
    clearChanges: () => {
        setChangedState(false);
    },

    /**
     * This will purge the cached data in the grid. Forcing it to reload the data from the server when we use it.
     * @returns
     */
    purgeCache: () => {
        gridComp.value?.purgeCachedData();
    },

    ensureRowVisible: (rowId: string) => {
        gridComp.value?.ensureRowVisible(rowId);
    },

    /**
     * If it is a master detail grid then we need to save the detail grids that have changes
     * @returns
     */
    saveGridDetail: async () => {
        // This will trigger the save of the detail views
        await gridComp.value?.saveDetailGrids();
    },

    /**
     * Will refresh and redraw the cells in the grid
     * @param rowIds
     * @param columns
     * @returns
     */
    refreshCells: (rowIds: string[], columns?: string[]) => {
        gridComp.value?.refreshCells(rowIds, columns);
    },

    /**
     * Sets the sort state of the grid
     * This will override any existing sort state
     * @param newSortState The new sorting state
     */
    setSortState: (newSortState: IGridColumnSort[]) => {
        gridComp.value?.setSortState(newSortState);
    },

    /**
     * Exports the grid data to excel
     */
    exportToExcel: async (fileName: string, sheetName: string) => {
        await gridComp.value?.exportToExcel(fileName, sheetName);
    },
});

// TODO / MARK:
// This function is here to force ListDetailModal to have correct type of T. It is not infered correctly from the only thing we use T for, namely the saving of the detail.
// We need T to be refered in the parameters in. The type is set from detailview as a string. We should probably find a way to send that as a Type and not get this problem.
const THelper = (a: TObj) => {
    return a;
};

/**
 * Computed that checks if any of the rows in the data has changes
 */
const hasModifiedRowsInData = computed(() => props.modelValue.some((r) => r.EntityState != 'Unchanged'));

/**
 * This is the sum of all the changes in the list
 */
const internalIsChanged = computed(() => {
    let isDetailChanged = listCompState.value.DetailGrids.some((d) => d.IsChanged);

    emit('detailchanged', isDetailChanged);

    return listCompState.value.IsChanged || isDetailChanged || hasModifiedRowsInData.value;
});

/**
 * Helper to check if there is any fields with warnings
 */
const hasFieldsWithWarnings = computed(() => {
    return (listCompState.value.WarningFields?.size ?? 0) > 0;
});

provide('ListCompState', listCompState);
</script>
<template>
    <div
        class="d-flex flex-column gap-3 h-100"
        ref="container"
    >
        <template v-if="!props.hideMessages">
            <AlertMessage
                ref="alertmessageElement"
                v-if="internalIsChanged"
                type="warning"
                >{{
                    $tm('general.unsavedchanges') + (additionalWarningOnChange ? ' ' + $tm(additionalWarningOnChange, undefined, true) : '')
                }}</AlertMessage
            >
            <InvalidValuesMessage
                ref="invalidValuesElement"
                v-if="listCompState.HasInvalidRows"
            />
            <DidYouKnowMessage
                v-if="props.listMode === false"
                message="grid.didyouknow"
            />
        </template>

        <ListCompToolbar
            v-if="!props.hideToolbar"
            ref="toolbarElement"
            v-bind="toolbar"
            v-model:search-text="toolbar.searchText.value"
            v-model:filtered-columns="filteredColumns"
            :action-menu-text="props.actionMenuText"
            :hide-search-field="props.hideSearchField"
            :hideDefaultButtons
            :show-error-filter="listCompState.HasInvalidRows || hasFieldsWithWarnings"
            :rows-with-notifications="numberOfRowsWithNotifications"
            :defaultMenuActions="defaultMenuActions"
            :columnDefs="props.gridkey ? gridSetup.GridColumnDefinition.ColumnOptions : undefined"
            @searching="onSearching"
        >
            <template v-slot:menu-filters><slot name="menu-filters"></slot></template>
        </ListCompToolbar>

        <slot
            v-else
            name="toolbar"
        ></slot>
        <GridComp
            :grid-setup="gridSetup"
            :grid-detail-view="props.gridDetailView"
            :grid-detail-title="props.gridDetailTitle"
            :list-mode="props.listMode"
            :resize-strategy="props.resizeStrategy"
            @on-selection-changed="onSelectionChanged"
            @on-cell-mouse-over="onCellMouseOver"
            @on-cell-mouse-out="onCellMouseOut"
            @on-cell-focused="onCellFocused"
            @on-first-data-rendered="onFirstDataRendered"
            @on-cell-key-down="($event: KeyboardEvent) => emit('cellKeyDown', $event)"
            @onIdColumnClicked="openDetail"
            @cellValueChanged="onCellValueChanged"
            @onCellEditingStarted="onCellEditingStarted"
            @onCellEditingStopped="onCellEditingStopped"
            @onStateUpdated="onStateUpdated"
            class="flex-grow-1"
            ref="gridComp"
        />

        <!-- it is important that dialog will be destroyed when id is 0. Otherwise we need to fix all the detailmodals to listen to change in id property -->
        <ListDetailModal
            v-if="detailView && viewActive && isVisible && currentDetailId != null"
            :detailView="detailView"
            :detailSize="detailSize"
            :detailViewParams="detailViewParams"
            :id="currentDetailId"
            :keepOpenOnSave="props.keepOpenOnSave"
            :save-state="savestate"
            :save-state-loader="props.saveStateLoader"
            :THelper="THelper"
            @closed="detailClosed"
            @saveDetail="saveDetail"
            @detailLoaded="onDetailLoaded"
            ref="detailModal"
            :footer-actions="detailFooterActions"
        />
    </div>
</template>
